import * as React from 'react';
import ClassNames from 'classnames';
import { KEY_VALUES, useControlled, mergeRefs, getActiveElement } from '../utils';
import { useLocalization } from "@appkit4/react-api";
var Tree = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var id = _ref.id,
    data = _ref.data,
    onClick = _ref.onClick,
    className = _ref.className,
    style = _ref.style,
    treeId = _ref.treeId,
    dataProp = _ref.dataTree,
    renderTreeNode = _ref.renderTreeNode,
    onUpdateData = _ref.onUpdateData;
  var treeRef = React.useRef(null);
  var shouldTab = React.useRef(false);
  var isFirstTab = React.useRef(true);
  var _useControlled = useControlled(dataProp, data || []),
    dataTree = _useControlled[0],
    setDataTree = _useControlled[1];
  var treeClasses = ClassNames('ap-tree', className, {});
  var allDataLinkedList = [];
  var isBranchNode = function isBranchNode(node) {
    return node.children != null && node.children.length > 0;
  };
  var _useLocalization = useLocalization(),
    t = _useLocalization.t;
  var getParent = function getParent(data, id) {
    var _data$find;
    return (_data$find = data.find(function (child) {
      return child.key === id;
    })) === null || _data$find === void 0 ? void 0 : _data$find.parent;
  };
  var getParentItem = function getParentItem(data, id) {
    var _data$find2;
    var parentId = getParent(data, id);
    return (_data$find2 = data.find(function (child) {
      return child.key === parentId;
    })) === null || _data$find2 === void 0 ? void 0 : _data$find2.item;
  };
  var getSibling = function getSibling(data, id, diff) {
    if (id === '') return;
    var parentId = getParent(data, id);
    var parents = data.filter(function (child) {
      return child.parent === parentId;
    });
    var index = parents === null || parents === void 0 ? void 0 : parents.findIndex(function (child) {
      return child.key === id;
    });
    if (parentId === undefined || parentId != null) {
      var _parents$siblingIndex;
      var siblingIndex = index + diff;
      if ((_parents$siblingIndex = parents[siblingIndex]) !== null && _parents$siblingIndex !== void 0 && _parents$siblingIndex.item) {
        var _parents$siblingIndex2;
        return (_parents$siblingIndex2 = parents[siblingIndex]) === null || _parents$siblingIndex2 === void 0 ? void 0 : _parents$siblingIndex2.item;
      } else {
        return null;
      }
    }
    return null;
  };
  var getNextAccessible = function getNextAccessible(data, id) {
    var _data$find3;
    var node = (_data$find3 = data.find(function (child) {
      return child.key === id;
    })) === null || _data$find3 === void 0 ? void 0 : _data$find3.item;
    var nodeId = node === null || node === void 0 ? void 0 : node.key;
    if (node && isBranchNode(node) && (node === null || node === void 0 ? void 0 : node.expanded) === true && node.children) {
      return node.children[0];
    }
    while (true) {
      var next = getSibling(data, nodeId || '', 1);
      if (next !== null) {
        return next;
      }
      nodeId = getParent(data, nodeId || '');
      // reached the root so there is no next accessible node
      if (nodeId === null) {
        return null;
      }
    }
  };
  var getPreviousAccessible = function getPreviousAccessible(data, id) {
    var _data$, _data$2;
    if ((_data$ = data[0]) !== null && _data$ !== void 0 && _data$.item && id === ((_data$2 = data[0]) === null || _data$2 === void 0 ? void 0 : _data$2.item.key)) {
      return null;
    }
    var previous = getSibling(data, id, -1);
    if (!previous) {
      return getParentItem(data, id);
    }
    return getLastAccessible(data, previous.key);
  };
  var getLastAccessible = function getLastAccessible(data, id) {
    var _data$find4;
    var node = (_data$find4 = data.find(function (child) {
      return child.key === id;
    })) === null || _data$find4 === void 0 ? void 0 : _data$find4.item;
    if (!node) return;
    while (node.expanded === true && isBranchNode(node) && node.children) {
      node = node.children[node.children.length - 1];
    }
    return node;
  };
  var resetData = function resetData(data) {
    data.forEach(function (child) {
      child.selected = false;
      if (child.children) {
        resetData(child.children);
      }
    });
  };
  var contentShowEvent = function contentShowEvent(event, item) {
    var _item$children;
    event === null || event === void 0 ? void 0 : event.stopPropagation();
    onClick === null || onClick === void 0 ? void 0 : onClick(item, event);
    if (!item.children || !((_item$children = item.children) !== null && _item$children !== void 0 && _item$children.length)) {
      return;
    }
    resetData(dataTree);
    item.expanded = !item.expanded;
    item.selected = true;
    setDataTree([].concat(dataTree));
    onUpdateData === null || onUpdateData === void 0 ? void 0 : onUpdateData([].concat(dataTree));
  };
  var hoverListItem = function hoverListItem(event, isHover) {
    var fileTitleEle = event.currentTarget.querySelector('.ap-file-title');
    if (isHover) {
      fileTitleEle === null || fileTitleEle === void 0 ? void 0 : fileTitleEle.classList.add('ap-font-medium');
    } else {
      fileTitleEle === null || fileTitleEle === void 0 ? void 0 : fileTitleEle.classList.remove('ap-font-medium');
    }
  };
  var getNextNodeMatchesKey = function getNextNodeMatchesKey(key, currentItem) {
    var currentId = currentItem.key;
    var currentNodeIndex = allDataLinkedList.findIndex(function (child) {
      return child.key === currentId;
    });
    var isMatch = function isMatch(item) {
      var parentItem = getParentItem(allDataLinkedList, item.key);
      return (!parentItem || parentItem.expanded === true) && item.title.trim().charAt(0).toLowerCase() === key;
    };
    var beforeNodes = [].concat(allDataLinkedList).slice(0, currentNodeIndex + 1);
    var afterNodes = [].concat(allDataLinkedList).slice(currentNodeIndex + 1);
    var firstMatchInAfterNode = afterNodes.filter(isMatch)[0];
    if (!firstMatchInAfterNode) {
      var firstMatchInBeforeNode = beforeNodes.filter(isMatch)[0];
      return firstMatchInBeforeNode ? firstMatchInBeforeNode.item : currentItem;
    }
    return firstMatchInAfterNode.item;
  };
  var alphabetEvent = function alphabetEvent(event, item) {
    if (event.keyCode > 64 && event.keyCode < 90) {
      var key = event.key;
      var nextNode = getNextNodeMatchesKey(key, item);
      resetData(dataTree);
      nextNode.selected = true;
      shouldFirstTab(nextNode.key);
      setDataTree([].concat(dataTree));
      onUpdateData === null || onUpdateData === void 0 ? void 0 : onUpdateData([].concat(dataTree));
      return;
    }
  };
  var onKeydownFile = function onKeydownFile(event, item) {
    event.stopPropagation();
    if (!shouldTab.current) event.preventDefault();
    switch (event.key) {
      case KEY_VALUES.ENTER:
        contentShowEvent(event, item);
        break;
      case KEY_VALUES.DOWN:
        downEventByItem(item);
        break;
      case KEY_VALUES.UP:
        upEventByItem(item);
        break;
      case KEY_VALUES.LEFT:
        leftEventByItem(item);
        break;
      case KEY_VALUES.RIGHT:
        rightEventByItem(item);
        break;
    }
    alphabetEvent(event, item);
    var tree = treeRef.current;
    if (!shouldTab.current && Array.prototype.indexOf.call(tree.querySelectorAll('.ap-files-fold'), getActiveElement()) === 0) {
      shouldTab.current = true;
      isFirstTab.current = true;
    }
  };
  var downEventByItem = function downEventByItem(item) {
    var nextItem = getNextAccessible(allDataLinkedList, item.key);
    if (nextItem) {
      resetData(dataTree);
      nextItem.selected = true;
      setDataTree([].concat(dataTree));
      onUpdateData === null || onUpdateData === void 0 ? void 0 : onUpdateData([].concat(dataTree));
    }
  };
  var upEventByItem = function upEventByItem(item) {
    var prevItem = getPreviousAccessible(allDataLinkedList, item.key);
    if (prevItem) {
      resetData(dataTree);
      prevItem.selected = true;
      setDataTree([].concat(dataTree));
      onUpdateData === null || onUpdateData === void 0 ? void 0 : onUpdateData([].concat(dataTree));
    }
  };
  var leftEventByItem = function leftEventByItem(item) {
    resetData(dataTree);
    if (item.children && item.expanded) {
      item.expanded = false;
      setDataTree([].concat(dataTree));
      onUpdateData === null || onUpdateData === void 0 ? void 0 : onUpdateData([].concat(dataTree));
    } else {
      var parentItem = getParentItem(allDataLinkedList, item.key);
      if (parentItem) {
        parentItem.selected = true;
        setDataTree([].concat(dataTree));
        onUpdateData === null || onUpdateData === void 0 ? void 0 : onUpdateData([].concat(dataTree));
      }
    }
  };
  var rightEventByItem = function rightEventByItem(item) {
    if (item.children) {
      resetData(dataTree);
      if (!item.expanded) {
        item.expanded = true;
        setDataTree([].concat(dataTree));
        onUpdateData === null || onUpdateData === void 0 ? void 0 : onUpdateData([].concat(dataTree));
      } else {
        downEventByItem(item);
      }
    }
  };
  var focusItemEvent = function focusItemEvent(event) {
    var _event$target$firstEl;
    (_event$target$firstEl = event.target.firstElementChild) === null || _event$target$firstEl === void 0 ? void 0 : _event$target$firstEl.classList.add('focus');
  };
  var blurItemEvent = function blurItemEvent(event) {
    var _event$target$firstEl2;
    (_event$target$firstEl2 = event.target.firstElementChild) === null || _event$target$firstEl2 === void 0 ? void 0 : _event$target$firstEl2.classList.remove('focus');
  };
  var mouseDownEvent = function mouseDownEvent() {
    var _treeRef$current;
    (_treeRef$current = treeRef.current) === null || _treeRef$current === void 0 ? void 0 : _treeRef$current.classList.add('no-outline');
  };
  var keyupEvent = function keyupEvent(event) {
    var _treeRef$current2;
    (_treeRef$current2 = treeRef.current) === null || _treeRef$current2 === void 0 ? void 0 : _treeRef$current2.classList.remove('no-outline');
    // if (event.key === KEY_VALUES.TAB) {
    //     shouldTab.current = true;
    //     const tree = (treeRef.current as HTMLElement);
    //     if (tree && tree.contains(document.activeElement)) {
    //         document.activeElement?.firstElementChild?.classList.add('focus');
    //     }   
    // }
  };

  var blurTreeEvent = function blurTreeEvent(event) {
    shouldTab.current = false;
    var blurKey = event.target.getAttribute('attr-key');
    // Prevent focusing the first element by default
    shouldFirstTab(blurKey);
  };
  var shouldFirstTab = function shouldFirstTab(lastTabKey) {
    if (allDataLinkedList[0].key !== lastTabKey) {
      isFirstTab.current = false;
    } else {
      isFirstTab.current = true;
    }
  };
  var renderContent = function renderContent(item, isParent) {
    if (isParent === void 0) {
      isParent = true;
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, isParent ? /*#__PURE__*/React.createElement("span", {
      className: "Appkit4-icon " + (item.expanded ? item.iconOpened : item.iconClosed),
      "aria-hidden": "true"
    }) : /*#__PURE__*/React.createElement("span", {
      className: "Appkit4-icon " + (item.icon ? item.icon : 'icon-paper-outline'),
      "aria-hidden": "true"
    }), /*#__PURE__*/React.createElement("span", {
      className: "ap-file-title"
    }, item.title));
  };
  React.useEffect(function () {
    var _treeRef$current3, _treeRef$current4;
    (_treeRef$current3 = treeRef.current) === null || _treeRef$current3 === void 0 ? void 0 : _treeRef$current3.addEventListener('mousedown', mouseDownEvent);
    (_treeRef$current4 = treeRef.current) === null || _treeRef$current4 === void 0 ? void 0 : _treeRef$current4.addEventListener("focusout", blurTreeEvent);
    document.addEventListener('keyup', keyupEvent);
    return function () {
      var _treeRef$current5, _treeRef$current6;
      (_treeRef$current5 = treeRef.current) === null || _treeRef$current5 === void 0 ? void 0 : _treeRef$current5.removeEventListener('mousedown', mouseDownEvent);
      (_treeRef$current6 = treeRef.current) === null || _treeRef$current6 === void 0 ? void 0 : _treeRef$current6.removeEventListener('focusout', blurTreeEvent);
      document.removeEventListener('keyup', keyupEvent);
    };
  }, []);
  var renderChildren = function renderChildren(dataChildren, isRoot, parentId) {
    return dataChildren.map(function (item, index) {
      item.parent = parentId;
      allDataLinkedList.push({
        key: item.key,
        title: item.title,
        item: item,
        parent: parentId
      });
      var itemRef = function itemRef(element) {
        if (item !== null && item !== void 0 && item.selected && element) {
          element.focus();
        }
      };
      if (item.children) return /*#__PURE__*/React.createElement("div", {
        className: ClassNames('ap-files-fold', {
          'active': item.expanded,
          'normal': !item.expanded
        }),
        role: "treeitem",
        ref: itemRef,
        key: item.key,
        "attr-key": item.key,
        onFocus: function onFocus(event) {
          return focusItemEvent(event);
        },
        onBlur: function onBlur(event) {
          return blurItemEvent(event);
        },
        onClick: function onClick(event) {
          return contentShowEvent(event, item);
        },
        onMouseEnter: function onMouseEnter(event) {
          return hoverListItem(event, true);
        },
        onMouseLeave: function onMouseLeave(event) {
          return hoverListItem(event, false);
        },
        tabIndex: isRoot && index === 0 && isFirstTab.current || item.selected ? 0 : -1,
        onKeyDown: function onKeyDown(event) {
          return onKeydownFile(event, item);
        },
        "aria-expanded": item.expanded,
        "aria-selected": item.selected
      }, /*#__PURE__*/React.createElement("div", {
        className: ClassNames("ap-fold-name")
      }, renderTreeNode ? renderTreeNode(item, true) : renderContent(item)), /*#__PURE__*/React.createElement("div", {
        className: ClassNames('ap-tree-list animated', {
          'fadeOutUp': !item.expanded,
          'fadeInDown': item.expanded
        }),
        role: "group"
      }, renderChildren(item.children, false, item.key)));
      return /*#__PURE__*/React.createElement("div", {
        className: "ap-files-fold",
        onMouseEnter: function onMouseEnter(event) {
          return hoverListItem(event, true);
        },
        onMouseLeave: function onMouseLeave(event) {
          return hoverListItem(event, false);
        },
        onClick: function onClick(event) {
          return contentShowEvent(event, item);
        },
        ref: itemRef,
        key: item.key,
        "attr-key": item.key,
        onFocus: function onFocus(event) {
          return focusItemEvent(event);
        },
        onBlur: function onBlur(event) {
          return blurItemEvent(event);
        },
        tabIndex: isRoot && index === 0 && isFirstTab.current || item.selected ? 0 : -1,
        role: "treeitem",
        onKeyDown: function onKeyDown(event) {
          return onKeydownFile(event, item);
        },
        "aria-expanded": item.expanded
      }, /*#__PURE__*/React.createElement("div", {
        className: ClassNames("ap-tree-list-item"),
        "aria-selected": item.selected
      }, renderTreeNode ? renderTreeNode(item, false) : renderContent(item, false)));
    });
  };
  return /*#__PURE__*/React.createElement("div", {
    id: id || treeId,
    className: treeClasses,
    style: style,
    role: "tree",
    ref: mergeRefs(ref, treeRef),
    "aria-label": t("aria").fileTreeLabel
  }, renderChildren(dataTree, true));
});
Tree.displayName = 'Tree';
export default Tree;